/**
 * CR382: S0.5 Status Badge + Config Summary
 * Displays S0.5 "Download Flyer" status on bucket source cards and diagnostics
 */

import { Badge } from "@/components/ui/badge";
import { Download, Image, FileDown, AlertCircle, CheckCircle, Clock, Loader2 } from "lucide-react";
import type { S05FlyerFetchMode, S05Status } from "@/lib/flyer-bucket/types";

interface S05ConfigBadgeProps {
  enabled: boolean;
  mode: S05FlyerFetchMode;
}

/** Shows S0.5 config state on the bucket sources list */
export function S05ConfigBadge({ enabled, mode }: S05ConfigBadgeProps) {
  if (!enabled) return null;

  const modeLabels: Record<S05FlyerFetchMode, { label: string; icon: React.ReactNode }> = {
    DOWNLOAD_ONLY: { label: "Download", icon: <FileDown className="h-3 w-3" /> },
    IMAGE_ONLY: { label: "Screenshot", icon: <Image className="h-3 w-3" /> },
    IMAGE_AND_DOWNLOAD: { label: "Both", icon: <Download className="h-3 w-3" /> },
  };

  const { label, icon } = modeLabels[mode] || modeLabels.DOWNLOAD_ONLY;

  return (
    <Badge variant="outline" className="text-xs gap-1">
      {icon}
      S0.5: {label}
    </Badge>
  );
}

interface S05RunStatusProps {
  status: S05Status | null;
  error?: string | null;
  downloadPath?: string | null;
  screenshotPrefix?: string | null;
  pageCount?: number | null;
  startedAt?: string | null;
  completedAt?: string | null;
}

/** Shows S0.5 run status in diagnostics panels */
export function S05RunStatus({
  status,
  error,
  downloadPath,
  screenshotPrefix,
  pageCount,
  startedAt,
  completedAt,
}: S05RunStatusProps) {
  if (!status) return null;

  const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
    PENDING: {
      color: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
      icon: <Clock className="h-3 w-3" />,
      label: "Pending",
    },
    RUNNING: {
      color: "bg-primary/10 text-primary border-primary/20",
      icon: <Loader2 className="h-3 w-3 animate-spin" />,
      label: "Running",
    },
    SUCCESS: {
      color: "bg-green-500/10 text-green-600 border-green-500/20",
      icon: <CheckCircle className="h-3 w-3" />,
      label: "Success",
    },
    FAILED: {
      color: "bg-red-500/10 text-red-600 border-red-500/20",
      icon: <AlertCircle className="h-3 w-3" />,
      label: "Failed",
    },
    SKIPPED: {
      color: "bg-muted text-muted-foreground border-border",
      icon: null,
      label: "Skipped",
    },
  };

  const config = statusConfig[status] || statusConfig.SKIPPED;

  const durationMs =
    startedAt && completedAt
      ? new Date(completedAt).getTime() - new Date(startedAt).getTime()
      : null;

  return (
    <div className="p-3 rounded-lg border bg-card space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Download className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">S0.5 Download Flyer</span>
        </div>
        <Badge variant="outline" className={`text-xs gap-1 ${config.color}`}>
          {config.icon}
          {config.label}
        </Badge>
      </div>

      {/* Details row */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {pageCount != null && (
          <span>{pageCount} page{pageCount !== 1 ? "s" : ""}</span>
        )}
        {durationMs != null && (
          <span>{Math.round(durationMs / 1000)}s</span>
        )}
        {downloadPath && (
          <span className="font-mono truncate max-w-[200px]" title={downloadPath}>
            📄 {downloadPath.split("/").pop()}
          </span>
        )}
        {screenshotPrefix && (
          <span className="font-mono truncate max-w-[200px]" title={screenshotPrefix}>
            📸 {screenshotPrefix}
          </span>
        )}
      </div>

      {/* Error */}
      {error && status === "FAILED" && (
        <div className="p-2 bg-red-500/5 rounded text-xs text-red-600 truncate" title={error}>
          {error.slice(0, 200)}
        </div>
      )}
    </div>
  );
}
